import { useCandlestickChart } from '../hooks/candlestick-chart.hook';
import { useIndicatorChart } from '../hooks/indicator-chart.hook';
import { useChartSynchronization } from '../hooks/chart-synchronisation.hook';
import { useChartResize } from '../hooks/use-chart-resize.hook';
import { usePriceScaleSynchronization } from '../hooks/use-price-scale-synchronization.hook';
import { useChartGroupTimeScaleSync } from '../hooks/use-chart-group-time-scale-sync.hook';
import { useCrosshairPriceLineSynchronization } from '../hooks/use-crosshair-price-line-synchronization.hook';
import { useFitContent } from '../hooks/fit-content.hook';
import { useChartContext } from './chart.context';
import { useChartStreamContext } from './chart-stream.context';
import { Box } from '@mui/material';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Tooltip } from './tooltip';

const RESIZE_HANDLE_SX = {
  height: 8,
  width: '100%',
  flex: '0 0 8px',
  cursor: 'row-resize',
  position: 'relative',
  backgroundColor: 'transparent',
  '&:hover': {
    '&::before': {
      backgroundColor: 'rgba(255,255,255,0.45)'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    transform: 'translateY(-0.5px)',
    height: '1px',
    backgroundColor: 'rgba(255,255,255,0.1)'
  }
} as const;

export const Chart = ({
  timeframeIndex,
  timeframe
}: {
  timeframeIndex?: number;
  timeframe?: string;
}) => {
  const {
    chartContainerRef,
    indicatorContainerRefs,
    tooltipRef,
    data: { indicatorConfigs }
  } = useChartContext();
  const { formState, timeframeIndex: streamTimeframeIndex } =
    useChartStreamContext();

  const { createCandlestickChart } = useCandlestickChart();
  useIndicatorChart();
  const timeScaleSync = useChartGroupTimeScaleSync();
  useChartSynchronization(timeframeIndex, timeframe, timeScaleSync);
  useCrosshairPriceLineSynchronization(timeframeIndex, timeframe);
  useChartResize();
  usePriceScaleSynchronization();
  useFitContent();

  const hasSubplots = indicatorConfigs.subplot.length > 0;
  const symbol = formState.marketConfig?.symbol ?? 'unknown-symbol';
  const basePanelsId = `chart-panels:${symbol}:${streamTimeframeIndex}`;
  const subplotSchemaKey = indicatorConfigs.subplot
    .map(({ type }, index) => `${type}-${index}`)
    .join('|');
  const subplotsPanelsId = `${basePanelsId}:subplots:${subplotSchemaKey}`;

  const mainChartBox = (
    <Box
      ref={(el: HTMLDivElement | null) => {
        chartContainerRef.current = el;

        if (el) {
          createCandlestickChart();
        }
      }}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        position: 'relative'
      }}
    >
      <Tooltip tooltipRef={tooltipRef} />
    </Box>
  );

  const subplotBox = (
    <PanelGroup
      direction="vertical"
      autoSaveId={subplotsPanelsId}
      style={{ height: '100%', width: '100%' }}
    >
      {indicatorConfigs.subplot.map(({ type }, index) => {
        const isLast = index === indicatorConfigs.subplot.length - 1;

        return [
          <Panel
            key={`subplot-panel-${type}-${index}`}
            id={`subplot-panel-${type}-${index}`}
            order={index * 2 + 1}
            defaultSize={100 / indicatorConfigs.subplot.length}
            minSize={8}
          >
            <Box
              ref={(el: HTMLDivElement | null) => {
                if (!indicatorContainerRefs.current) {
                  indicatorContainerRefs.current = {} as Record<
                    string,
                    HTMLDivElement
                  >;
                }

                if (!el) {
                  delete indicatorContainerRefs.current[type];
                  return;
                }

                indicatorContainerRefs.current[type] = el;
              }}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 0
              }}
            />
          </Panel>,
          !isLast ? (
            <PanelResizeHandle
              key={`subplot-handle-${type}-${index}`}
              id={`subplot-handle-${type}-${index}`}
            >
              <Box sx={RESIZE_HANDLE_SX} />
            </PanelResizeHandle>
          ) : null
        ];
      })}
    </PanelGroup>
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {hasSubplots ? (
        <PanelGroup
          direction="vertical"
          autoSaveId={basePanelsId}
          style={{ height: '100%', width: '100%' }}
        >
          <Panel id="main-panel" order={1} defaultSize={65} minSize={20}>
            {mainChartBox}
          </Panel>
          <PanelResizeHandle id="main-subplot-handle">
            <Box sx={RESIZE_HANDLE_SX} />
          </PanelResizeHandle>
          <Panel id="subplots-panel" order={3} defaultSize={35} minSize={15}>
            {subplotBox}
          </Panel>
        </PanelGroup>
      ) : (
        mainChartBox
      )}
    </Box>
  );
};
